import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaHome, 
  FaArrowLeft, 
  FaRocket, 
  FaCloud,
  FaQuestionCircle,
  FaSearch,
  FaRegSadTear,
  FaLightbulb
} from 'react-icons/fa';
import SEO from '../components/SEO/SEO';

const NotFoundPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [countdown, setCountdown] = useState(10);
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          setIsRedirecting(true);
          setTimeout(() => navigate('/'), 1000);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.8, ease: [0.6, -0.05, 0.01, 0.99] }
    }
  };

  const floatingVariants = {
    animate: {
      y: [-20, 20, -20],
      rotate: [0, 5, 0, -5, 0],
      transition: {
        duration: 6,
        repeat: Infinity,
        ease: 'easeInOut'
      }
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
    }
  };

  const quickLinks = [
    { name: 'Home', path: '/', icon: FaHome, color: 'from-blue-500 to-purple-500' },
    { name: 'Services', path: '/services/aws-services', icon: FaCloud, color: 'from-purple-500 to-pink-500' },
    { name: 'About Us', path: '/about', icon: FaLightbulb, color: 'from-green-500 to-blue-500' },
    { name: 'Contact', path: '/contact', icon: FaQuestionCircle, color: 'from-orange-500 to-red-500' }
  ];

  return (
    <>
      <SEO 
        title="Page Not Found - 404"
        description="The page you're looking for doesn't exist. Please check the URL or navigate back to our homepage."
        noindex={true}
      />
      
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 relative overflow-hidden flex items-center justify-center">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 z-0">
          <motion.div 
            className="absolute top-20 left-10 w-64 h-64 bg-blue-500/10 rounded-full mix-blend-multiply filter blur-xl"
            variants={floatingVariants}
            animate="animate"
          />
          <motion.div 
            className="absolute top-40 right-20 w-80 h-80 bg-purple-500/10 rounded-full mix-blend-multiply filter blur-xl"
            variants={floatingVariants}
            animate="animate"
            style={{ animationDelay: '2s' }}
          />
          <motion.div 
            className="absolute bottom-20 left-1/3 w-96 h-96 bg-cyan-500/10 rounded-full mix-blend-multiply filter blur-xl"
            variants={floatingVariants}
            animate="animate"
            style={{ animationDelay: '4s' }}
          />
        </div>

        {/* Floating Decorative Elements */}
        <div className="absolute inset-0 z-0">
          {[...Array(15)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-blue-400/30 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [-20, 20, -20],
                opacity: [0.3, 0.7, 0.3],
                scale: [1, 1.5, 1]
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2,
                ease: 'easeInOut'
              }}
            />
          ))}
        </div>

        <div className="relative z-10 container mx-auto px-4 py-16">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Main 404 Display */}
            <motion.div 
              className="mb-12"
              variants={itemVariants}
            >
              <motion.div
                className="relative inline-block"
                animate={{
                  scale: [1, 1.05, 1],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }}
              >
                <h1 className="text-9xl md:text-[12rem] font-black bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent leading-none">
                  404
                </h1>
                <motion.div
                  className="absolute -top-4 -right-4 text-4xl md:text-6xl"
                  animate={{
                    rotate: [0, 10, -10, 0],
                    scale: [1, 1.1, 1]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut'
                  }}
                >
                  <FaRegSadTear className="text-yellow-500" />
                </motion.div>
              </motion.div>
            </motion.div>

            {/* Error Message */}
            <motion.div 
              className="mb-12"
              variants={itemVariants}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
                Oops! Page Not Found
              </h2>
              <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
                The page you're looking for seems to have wandered off into the cloud. 
                Don't worry, we'll help you find your way back!
              </p>
            </motion.div>

            {/* Search Box */}
            <motion.div 
              className="mb-12"
              variants={itemVariants}
            >
              <form onSubmit={handleSearch} className="max-w-md mx-auto">
                <div className="relative group">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="What are you looking for?"
                    className="w-full px-6 py-4 pl-12 text-lg bg-white/80 backdrop-blur-sm border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-blue-500 focus:bg-white transition-all duration-300 shadow-lg hover:shadow-xl group-focus-within:shadow-xl"
                    aria-label="Search for content"
                  />
                  <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl group-focus-within:text-blue-500 transition-colors duration-300" />
                  <motion.button
                    type="submit"
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl hover:from-blue-600 hover:to-purple-600 transition-all duration-300 shadow-md hover:shadow-lg"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    aria-label="Search"
                  >
                    <FaSearch />
                  </motion.button>
                </div>
              </form>
            </motion.div>

            {/* Quick Navigation Links */}
            <motion.div 
              className="mb-12"
              variants={itemVariants}
            >
              <h3 className="text-xl font-semibold text-gray-700 mb-6">
                Or try these popular sections:
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
                {quickLinks.map((link, index) => {
                  const IconComponent = link.icon;
                  return (
                    <motion.div
                      key={link.name}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 + index * 0.1 }}
                      whileHover={{ y: -5 }}
                      className="group"
                    >
                      <Link
                        to={link.path}
                        className="block p-6 bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl hover:shadow-xl transition-all duration-300 group-hover:border-transparent group-hover:shadow-2xl"
                        aria-label={`Navigate to ${link.name}`}
                      >
                        <div className={`w-12 h-12 mx-auto mb-4 bg-gradient-to-r ${link.color} rounded-xl flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                          <IconComponent className="text-xl" />
                        </div>
                        <h4 className="font-semibold text-gray-800 group-hover:text-gray-900 transition-colors duration-300">
                          {link.name}
                        </h4>
                      </Link>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>

            {/* Auto-redirect Notice */}
            <motion.div 
              className="mb-8"
              variants={itemVariants}
            >
              <AnimatePresence>
                {!isRedirecting ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    className="inline-flex items-center px-6 py-3 bg-blue-500/20 backdrop-blur-sm border border-blue-300 rounded-full text-blue-700 font-medium"
                  >
                    <FaRocket className="mr-2 text-blue-500" />
                    Redirecting to home in {countdown} seconds
                  </motion.div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="inline-flex items-center px-6 py-3 bg-green-500/20 backdrop-blur-sm border border-green-300 rounded-full text-green-700 font-medium"
                  >
                    <FaRocket className="mr-2 text-green-500" />
                    Redirecting now...
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Manual Navigation */}
            <motion.div 
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
              variants={itemVariants}
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="/"
                  className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-semibold rounded-2xl hover:from-blue-600 hover:to-purple-600 transition-all duration-300 shadow-lg hover:shadow-xl"
                  aria-label="Go to homepage"
                >
                  <FaHome className="mr-3 text-lg" />
                  Take Me Home
                </Link>
              </motion.div>
              
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <button
                  onClick={() => window.history.back()}
                  className="inline-flex items-center px-8 py-4 bg-white/80 backdrop-blur-sm border-2 border-gray-300 text-gray-700 font-semibold rounded-2xl hover:bg-white hover:border-gray-400 transition-all duration-300 shadow-lg hover:shadow-xl"
                  aria-label="Go back to previous page"
                >
                  <FaArrowLeft className="mr-3 text-lg" />
                  Go Back
                </button>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default NotFoundPage; 